import { Badge } from '@/components/ui/catalyst/badge';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { getArtifactTypeLabel } from '../utils/artifactFormatters';
import { ArtifactIcon } from './ArtifactIcon';
import { UnreadableBlobList } from './UnreadableBlobList';

interface ArtifactStatsData {
  total: number;
  withDigest: number;
  typeStats: Record<ArtifactType, number>;
}

interface ArtifactStatsProps {
  stats: ArtifactStatsData;
  className?: string;
  onTypeFilter?: (type: ArtifactType | 'unprocessed') => void;
  activeFilter?: ArtifactType | 'unprocessed' | 'all';
  matterId?: string; // Optional: enable unreadable blob list when provided
}

export const ArtifactStats = ({
  stats,
  className = '',
  onTypeFilter,
  activeFilter,
  matterId,
}: ArtifactStatsProps) => {
  const unprocessedCount = stats.total - stats.withDigest;

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
        {/* Processed/Total Summary */}
        <span className="text-muted-foreground/70 font-medium">
          {stats.withDigest} of {stats.total} processed
        </span>

        {/* Type Filter Badges */}
        {Object.entries(stats.typeStats).map(([type, count]) => {
          if (count === 0) return null;

          const artifactType = type as ArtifactType;
          const label = getArtifactTypeLabel(artifactType);
          const isActive = activeFilter === type;

          return (
            <button
              key={type}
              type="button"
              onClick={() => onTypeFilter?.(artifactType)}
              className={`transition-all ${isActive ? 'scale-105' : 'hover:scale-105'}`}
            >
              <Badge
                color={isActive ? 'blue' : 'zinc'}
                className="flex items-center gap-1.5"
              >
                <ArtifactIcon
                  type={artifactType}
                  className="h-3 w-3"
                  isSelected={isActive}
                />
                <span>
                  {count} {label.toLowerCase()}
                </span>
              </Badge>
            </button>
          );
        })}

        {/* Unprocessed Filter Badge - Last */}
        {unprocessedCount > 0 && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => onTypeFilter?.('unprocessed')}
              className={`transition-all ${
                activeFilter === 'unprocessed' ? 'scale-105' : 'hover:scale-105'
              }`}
            >
              <Badge
                color={activeFilter === 'unprocessed' ? 'amber' : 'zinc'}
                className="flex items-center gap-1"
              >
                <div className="h-2 w-2 bg-amber-500 rounded-full" />
                <span>{unprocessedCount} unprocessed</span>
              </Badge>
            </button>
          </div>
        )}

        {/* Unreadable blobs list (optional section) */}
        {matterId && <UnreadableBlobList matterId={matterId} />}
      </div>
    </div>
  );
};
